import { merge, isEmpty } from 'lodash';
import { ajaxRequest, replaceKeyName } from 'app/utils/helpers';
import { customFetch } from 'app/utils/ajaxHelpers';
import { getGlobalWishlistInfo, getLoggedUserInfo, loadNotifyMeClearanceStatuses } from 'app/utils/globalDataHelpers';
import { showError } from 'app/globalMessages/globalMessagesClient';
import {
  dlTrackLoadedProduct,
  dlTrackVirtualProductPage,
  dlViewItem,
  promoProductViewAbtasty,
} from 'app/analytics/loadedProductAnalytics';
import {
  DETAILS_LOADED,
  ADD_TO_COLLECTION,
  UPDATE_IN_WISHLIST_PRODUCTS,
  PRODUCT_NOT_FOUND,
  FETCH_USER_ENCODED_ID,
} from './productDetailsConstants';
import { sizeSelected } from './sizeSelector/sizeSelectorActions';

const { locale, abTastyBestSellerThreshold } = window.inlineGlobalConfig;
const { postRecentlyViewedProduct, recentlyViewedProductsURL, pageId, productNotFoundPage } =
  window.inlineProductDetails;

export const updateInWishlistProducts = () => (dispatch, getState) =>
  getGlobalWishlistInfo().then(wishlistInfo => {
    const { loadedProduct, loadedProducts } = getState().detailsPage;

    const wishlistEntries = wishlistInfo.wishlist.entries
      ? wishlistInfo.wishlist.entries.map(product => product.baseId)
      : [];
    const loadedProductInWishlist = Object.assign(loadedProduct, {
      inWishlist: wishlistEntries.includes(loadedProduct.productCode),
    });
    const loadedProductsInWishlist = loadedProducts.reduce(
      (acc, cur) =>
        acc.concat([
          Object.assign(cur, {
            inWishlist: wishlistEntries.includes(cur.productCode),
          }),
        ]),
      []
    );

    return dispatch({
      type: UPDATE_IN_WISHLIST_PRODUCTS,
      loadedProduct: loadedProductInWishlist,
      loadedProducts: loadedProductsInWishlist,
    });
  });

const getProductUrlFragments = url => {
  const { pathname, search, hash } = new URL(window.location.origin + url);

  const urlParams = new URLSearchParams(search);
  const styleParam = urlParams.has('colour') ? urlParams.get('colour').split() : '';

  try {
    const fullProductCodeRegexp = /[a-zA-Z|\d]{3,5}-[a-zA-Z|\d]{3,4}$/g;
    const baseProductCodeRegexp = /[a-zA-Z|\d]{1,5}$/g;
    const productCode = (pathname.match(fullProductCodeRegexp) || pathname.match(baseProductCodeRegexp))[0];
    const pathElements = pathname.split('/');
    const productPrefix = urlParams.has('previewProduct')
      ? `/${pathElements[1]}/${pathElements[2]}/shop/preview-p-`
      : pathname.substring(0, pathname.indexOf(productCode));
    const productStyle = styleParam || (hash ? hash.match(/[A-Za-z\d]{3,4}/g) : []) || productCode.split('-')[1] || '';
    const productCodeFull = urlParams.has('previewProduct')
      ? urlParams.get('previewProduct')
      : `${productCode.split('-')[0]}${productStyle.length ? `-${productStyle[0]}` : ''}`;

    return {
      productPrefix,
      productCodeFull,
      productQuery: search,
    };
  } catch (e) {
    throw Error(`Something went wrong with URL parsing: ${e}`);
  }
};

const detailsLoadedAction = product => dispatch => {
  const mappedProduct = replaceKeyName(product, ['prices', 'wasPrices'], ['price', 'wasPrice']);
  if (mappedProduct.hasStock) {
    dispatch(sizeSelected(null));
  }
  dispatch({ type: DETAILS_LOADED, data: mappedProduct });
  return mappedProduct;
};

const emitAnalytics = mappedProduct => {
  dlTrackLoadedProduct(mappedProduct);
  dlViewItem(mappedProduct);
  promoProductViewAbtasty(mappedProduct);
  dlTrackVirtualProductPage(mappedProduct.dataLayerName);
  return Promise.resolve(mappedProduct);
};

const updateBestSellerRankingData = mappedProduct => {
  const isBestSeller =
    mappedProduct.bestSellerRanking != null && mappedProduct.bestSellerRanking <= abTastyBestSellerThreshold;
  document.getElementById('pdpDetails')?.setAttribute('data-best-selling', `${isBestSeller}`);
  return Promise.resolve(mappedProduct);
};

const addProductToCollection = json => dispatch => dispatch({ type: ADD_TO_COLLECTION, product: json });

const custom404ErrorHandler = error => {
  showError({
    messageId: 'cart.update.error',
    sticky: true,
  });
  throw Error(`${error} (404 Error)`);
};

const notFoundHandler = error => ({
  status: 404,
  redirectHandler() {
    custom404ErrorHandler(error);
  },
});

const setHashFragment = (pathname, query, json) => {
  const [code, style] = json.productCode.split('-');

  const replaceUrl = () => {
    window.history.replaceState({}, '', `${pathname}${code}${query}${style ? `#${style}` : ''}`);
  };

  if (document.readyState !== 'complete') {
    window.addEventListener('load', () => {
      replaceUrl();
    });
  } else {
    replaceUrl();
  }

  return Promise.resolve(json);
};

const postRecentlyViewed = json => {
  if (postRecentlyViewedProduct) {
    ajaxRequest('GET', `${recentlyViewedProductsURL}/add`, {
      productCode: json.product.productCode,
    });
  }
  return Promise.resolve(json);
};

const getContentSlotsUrl = (type = 'json', positions, page, productCode) =>
  `/${locale.country}/${locale.language}/cms/${page}` +
  `/content-slot.${type}` +
  `?positions=${positions.join(':')}` +
  `&product=${productCode}`;

const contentSlotsReducer = csJsonFull =>
  Object.entries(csJsonFull)
    .filter(entry => entry[1] !== null)
    .reduce(
      (acc, [key, val]) =>
        val.components.length > 0
          ? {
              ...acc,
              [key]: {
                ...val,
                components: val.components.filter(comp => comp.visible !== false),
              },
            }
          : { ...acc },
      {}
    );

const fetchContentSlotsDataForProductNotFoundPage = productCode => {
  const CONTENT_SLOTS_MAP = {
    json: ['CrossSelling'],
  };

  return ajaxRequest(
    'GET',
    getContentSlotsUrl('json', CONTENT_SLOTS_MAP.json, productNotFoundPage, productCode),
    {},
    notFoundHandler('Unable to fetch JSON content slots')
  ).then(csJsonFull => {
    const csJson = contentSlotsReducer(csJsonFull);
    return Promise.resolve({ contentSlotsJson: csJson });
  });
};

const fetchContentSlotsData = json => {
  const CONTENT_SLOTS_MAP = {
    json: [
      'deliveryReturn',
      'SizeGuide',
      'Breadcrumb',
      'CrossSelling',
      'UpperProductSpecificContent',
      'LowerProductSpecificContent',
    ],
    html: ['SizeGuide', 'UpperProductSpecificContent', 'LowerProductSpecificContent'],
  };

  return ajaxRequest(
    'GET',
    getContentSlotsUrl('json', CONTENT_SLOTS_MAP.json, pageId, json.productCode),
    {},
    notFoundHandler('Unable to fetch JSON content slots')
  ).then(csJsonFull => {
    const csJson = contentSlotsReducer(csJsonFull);
    const getHtmlSlotTitlesWithContent = slot => (slot in csJson && csJson[slot].components.length > 0 ? slot : '');
    const requiredHtmlSlotTitles = CONTENT_SLOTS_MAP.html
      .map(getHtmlSlotTitlesWithContent)
      .filter(title => title.length > 0);
    return requiredHtmlSlotTitles.length
      ? ajaxRequest(
          'GET',
          getContentSlotsUrl('html', requiredHtmlSlotTitles, pageId, json.productCode),
          {},
          notFoundHandler('Unable to fetch HTML content slots')
        ).then(csHtml =>
          Promise.resolve({
            ...json,
            contentSlotsJson: csJson,
            contentSlotsHtml: csHtml,
          })
        )
      : Promise.resolve({ ...json, contentSlotsJson: csJson });
  });
};

// TODO rename/refactor function to reflect the actual actions performed here
export const fetchProductDetails = url => (dispatch, getState) => {
  const { productPrefix, productQuery, productCodeFull } = getProductUrlFragments(url);

  const dynamicProductPrefix = productPrefix.replace(/-p-$/, '-dyn-');

  const isProductLoaded = getState().detailsPage.loadedProducts.find(
    product => product.productCode === productCodeFull
  );

  const onLoadingProductData = xhttp =>
    new Promise((resolve, reject) => {
      xhttp.onload = response => {
        const { responseText } = response.target;
        try {
          const productData = JSON.parse(responseText);
          return resolve(productData);
        } catch (e) {
          return reject(responseText);
        }
      };
    });

  const errorHandler = async err => {
    const productNotFoundContentSlotsData = await fetchContentSlotsDataForProductNotFoundPage(productCodeFull);
    dispatch({
      type: PRODUCT_NOT_FOUND,
      payload: { notFoundContent: err, loadedProduct: productNotFoundContentSlotsData },
    });
    console.warn(err);
    return Promise.reject(err);
  };

  const fetchProductData = productInCollection =>
    !productInCollection
      ? Promise.all([
          customFetch('GET', `${productPrefix + productCodeFull}.json`, {}).then(xhttp => onLoadingProductData(xhttp)),
          customFetch('GET', `${dynamicProductPrefix + productCodeFull}.json`, {}).then(xhttp =>
            onLoadingProductData(xhttp)
          ),
        ])
          .then(
            productJsons => {
              return Promise.resolve(merge(productJsons[0], productJsons[1]));
            },
            err => errorHandler(err)
          )
          .then(productData => {
            if (!productData) {
              return Promise.reject(new Error('No product data returned'));
            }
            return fetchContentSlotsData(productData);
          })
          .then(json => {
            if (!json) {
              return Promise.reject(new Error('No product data returned'));
            }

            return dispatch(addProductToCollection(json));
          })
          .then(json => {
            return postRecentlyViewed(json);
          })
      : Promise.resolve({ product: productInCollection });

  return Promise.all([fetchProductData(isProductLoaded), loadNotifyMeClearanceStatuses()])
    .then(([json]) => dispatch(detailsLoadedAction(json.product)))
    .then(json => setHashFragment(productPrefix, productQuery, json))
    .then(json => emitAnalytics(json))
    .then(json => updateBestSellerRankingData(json))
    .then(json => dispatch(updateInWishlistProducts(json)));
};

export const fetchUserEncodedId = () => dispatch => {
  try {
    getLoggedUserInfo().then(userInfo => {
      if (!isEmpty(userInfo['userInfo/index'])) {
        dispatch({
          type: FETCH_USER_ENCODED_ID,
          data: userInfo['userInfo/index'].encodedId,
        });
      }
    });
  } catch (err) {
    console.error(err);
  }
};
